/**
 * "Online access" box of a title harvested from a digital resource: opens the
 * title on the provider's platform, the patron already signed in.
 */

import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { usePatronAuth } from "../contexts/PatronAuthContext";
import { useOpenDigitalResource } from "./useDigitalResources";

export interface OnlineAccess {
  resourceId: string;
  name: string;
  category: string;
}

interface OnlineAccessCardProps {
  recordId: string;
  access: OnlineAccess;
}

export function OnlineAccessCard({ recordId, access }: OnlineAccessCardProps) {
  const { isAuthenticated } = usePatronAuth();
  const navigate = useNavigate();
  const { open, pendingId, error } = useOpenDigitalResource();
  const pending = pendingId === access.resourceId;

  const handleClick = () => {
    if (!isAuthenticated) {
      void navigate("/opac/login", { state: { from: `/opac/record/${recordId}` } });
      return;
    }
    void open({ id: access.resourceId, name: access.name }, recordId);
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/60 dark:bg-blue-950/30">
      <h3 className="font-semibold text-gray-900 dark:text-white">Accès en ligne</h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Disponible sur <span className="font-medium">{access.name}</span> (
        {access.category.toLowerCase()}), inclus dans votre abonnement.
      </p>
      <button
        type="button"
        onClick={handleClick}
        disabled={pending}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-wait disabled:opacity-70 dark:focus:ring-offset-gray-900"
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
        ) : (
          <ExternalLink className="h-4 w-4" aria-hidden />
        )}
        {isAuthenticated ? `Lire sur ${access.name}` : "Se connecter pour accéder"}
        {isAuthenticated && <span className="sr-only"> (s&apos;ouvre dans un nouvel onglet)</span>}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {error.message}
        </p>
      )}
    </div>
  );
}
